import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { Observable } from 'rxjs/internal/Observable';
import { lastValueFrom } from 'rxjs/internal/lastValueFrom';

import { SharedUtilsService } from './shared-utils.service';
import { AuthService } from './auth.service';
import { AdminSettingsService } from './admin-settings.service';


// Shared resolver constants used by loadHolidayCalendar / resolveHolidayMap below.
// Mirrors the backend resolver in server/tools/yeehro-calendly-booking-tools.js so
// any consumer can display the same date for a holiday entry without an extra
// round-trip. Keep these in sync if either side changes.
const HOLIDAY_SETTING_KEY: string = 'booking-holiday-schedule';
const HOLIDAY_WEEKDAY_NAMES: string[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const HOLIDAY_NTH_MAP: { [key: string]: number } = { first: 1, second: 2, third: 3, fourth: 4 };


@Injectable({ providedIn: 'root' })
export class YeehroCalendlyUserSettingsService {
  private API_URL = '/api/yeehro-calendly-user-settings/';
  private headerOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };

  bookingBaseUrl: string = 'yeehro.com/booking/';

  // ── Holiday calendar cache (shared across all consumers in the app) ────
  // Loaded once via ensureHolidayCalendarLoaded(); year-specific resolved
  // maps are filled lazily on first lookup. Idempotent — multiple callers
  // share one in-flight fetch via _holidayLoadPromise.
  private _holidayEntries: any[] = [];
  private _holidayMapsByYear: Map<number, Map<string, { scheduleStatus: string; name: string }>> = new Map();
  private _holidayLoadPromise: Promise<void> | null = null;

  constructor(
    private httpClient: HttpClient,
    private _sharedUtilsService: SharedUtilsService,
    private _authService: AuthService,
    private _adminSettingsService: AdminSettingsService
  ) {}


  // Fetch the current scheduling feature flag. Always hits the server — no
  // client-side caching — so each portal entry picks up admin changes (an admin
  // revoking access mid-session blocks the user on next portal re-entry). Hits
  // the dedicated public endpoint (no auth required) so the public booking
  // portal can call this for unauthenticated visitors. Returns false on any
  // failure (System-Admin-only safe fallback).
  async loadSchedulingFeatureEnabled(): Promise<boolean> {
    try {
      const status: any = await lastValueFrom(
        this.httpClient.get<any>(this.API_URL + 'scheduling-feature-enabled-status')
      );
      return status?.enabled === true;
    } catch (err) {
      console.log('Error loading scheduling feature enabled status: ', err);
      return false;
    }
  }


  // Centralized access gate for the booking flow.
  async canAccessBookingFlow(): Promise<boolean> {
    // Check if admin setting exists and is enabled.
    const enabled = await this.loadSchedulingFeatureEnabled();
    if (enabled) return true; // Enabled so return true now.

    const currentUser = this._authService?.getCurrentUserInStorage();
    if (!currentUser?._id || !currentUser?.userType) return false; // No user so return false now.

    // List of emails that should not get through while disabled.
    const excludedEmails = ['lnicol@eduhealthcare.com'];
    const email = String(currentUser?.email || '').trim().toLowerCase();

    // Return true if admin user and not in exclusion list.
    return currentUser?.userType === 'System Admin' && !excludedEmails.includes(email);
  }


  getOrCreateForUser(userId: string): Observable<any> {
    return this.httpClient.get<any>(this.API_URL + 'user/' + userId);
  }


  checkUsername(username: string, userId?: string): Observable<any> {
    let url = this.API_URL + 'check-username/' + username;
    if (userId) url += '?userId=' + userId;
    return this.httpClient.get<any>(url);
  }


  update(settings: any): Observable<any> {
    return this.httpClient.put<any>(this.API_URL + settings._id, JSON.stringify(settings), this.headerOptions);
  }


  // ── Holiday calendar (read-only consumers) ─────────────────────────────
  // Returns the raw entries array saved on the admin setting. Returns [] when
  // the setting is missing or any error occurs — callers can treat empty as
  // "no holidays configured" and render normally.
  async loadHolidayCalendar(): Promise<any[]> {
    try {
      const setting: any = await lastValueFrom(this._adminSettingsService.getByKey(HOLIDAY_SETTING_KEY));
      const raw: any = setting?.setting;
      return Array.isArray(raw) ? raw : [];
    } catch (err) {
      console.log('Error loading holiday calendar: ', err);
      return [];
    }
  }


  // Ensure the holiday calendar entries are loaded. Idempotent and shared —
  // any number of components calling this race-free with one underlying fetch.
  ensureHolidayCalendarLoaded(): Promise<void> {
    if (this._holidayLoadPromise) return this._holidayLoadPromise;
    this._holidayLoadPromise = (async () => {
      this._holidayEntries = await this.loadHolidayCalendar();
      this._holidayMapsByYear.clear();
    })();
    return this._holidayLoadPromise;
  }


  // Force a refresh — e.g. admin saved the holiday-schedule setting and the
  // open pages should pick up the new shape on next render.
  async reloadHolidayCalendar(): Promise<void> {
    this._holidayLoadPromise = null;
    this._holidayMapsByYear.clear();
    await this.ensureHolidayCalendarLoaded();
  }


  // Lookup. Returns null when the date isn't a holiday OR entries aren't loaded.
  // Year-specific maps are computed lazily and cached for subsequent lookups.
  getHolidayForDate(date: Date | string | null | undefined): { scheduleStatus: string; name: string } | null {
    if (!date || !this._holidayEntries?.length) return null;
    const d: Date = date instanceof Date ? date : new Date(date);
    if (isNaN(d?.getTime())) return null;

    const year: number = d.getFullYear();
    let map: Map<string, { scheduleStatus: string; name: string }> | undefined = this._holidayMapsByYear.get(year);
    if (!map) {
      map = this.resolveHolidayMap(this._holidayEntries, year);
      this._holidayMapsByYear.set(year, map);
    }
    return map.get(this._formatHolidayDate(d)) || null;
  }


  // Returns the holiday hits for an override group's date array. Most groups are
  // a single date; for multi-day groups, each distinct hit is returned once.
  getOverrideHolidayHits(dates: any[]): { name: string; scheduleStatus: string }[] {
    if (!Array.isArray(dates) || !dates.length) return [];
    const hits: { name: string; scheduleStatus: string }[] = [];
    const seen: Set<string> = new Set();
    for (const raw of dates) {
      const hit: { scheduleStatus: string; name: string } | null = this.getHolidayForDate(raw);
      if (!hit) continue;
      const key: string = `${hit.name}|${hit.scheduleStatus}`;
      if (seen.has(key)) continue;
      seen.add(key);
      hits.push({ name: hit.name, scheduleStatus: hit.scheduleStatus });
    }
    return hits;
  }


  // Build a Map keyed by 'YYYY-MM-DD' for the given year. Skips entries marked
  // 'working' (no UI signal needed) and any malformed rows. Two passes so anchored
  // entries (e.g. Christmas Eve = workday before Christmas) resolve against pass-1
  // dates without an extra round-trip.
  resolveHolidayMap(entries: any[], year: number): Map<string, { scheduleStatus: string; name: string }> {
    const map: Map<string, { scheduleStatus: string; name: string }> = new Map();
    if (!Array.isArray(entries) || !entries.length || !year) return map;

    // Pass 1 — fixed / floating / easter-relative entries.
    const resolved: { [key: string]: Date } = {};
    for (const entry of entries) {
      if (!entry || entry?.scheduleStatus === 'working') continue;
      if (entry?.type === 'anchored-weekday-offset') continue;

      const date: Date | null = this._computeHolidayDate(entry, year, null);
      if (!date) continue;

      if (entry?.holidayKey) resolved[entry.holidayKey] = date;

      const key: string = this._formatHolidayDate(date);
      if (!map.has(key)) {
        map.set(key, { scheduleStatus: entry?.scheduleStatus, name: entry?.name || '' });
      }
    }

    // Pass 2 — anchored entries that depend on pass-1 anchors.
    for (const entry of entries) {
      if (!entry || entry?.scheduleStatus === 'working') continue;
      if (entry?.type !== 'anchored-weekday-offset') continue;

      const date: Date | null = this._computeHolidayDate(entry, year, resolved);
      if (!date) continue;

      const key: string = this._formatHolidayDate(date);
      if (!map.has(key)) {
        map.set(key, { scheduleStatus: entry?.scheduleStatus, name: entry?.name || '' });
      }
    }

    return map;
  }


  // ── Internal: holiday date math (mirrors backend BookingTools resolver) ─

  private _computeHolidayDate(entry: any, year: number, resolved: { [key: string]: Date } | null): Date | null {
    if (!entry || !year) return null;
    let date: Date | null = null;

    if (entry?.type === 'fixed') {
      if (!entry?.month || !entry?.day) return null;
      date = new Date(year, entry.month - 1, entry.day);
    } else if (entry?.type === 'floating') {
      date = this._nthWeekdayOfMonth(year, entry?.month, entry?.weekday, entry?.occurrence);
      const off: number = Number(entry?.offset) || 0;
      if (date && off) date = new Date(date.getFullYear(), date.getMonth(), date.getDate() + off);
    } else if (entry?.type === 'easter-relative') {
      const easter: Date | null = this._easterSundayForYear(year);
      const off: number = Number(entry?.offset) || 0;
      if (easter) date = new Date(easter.getFullYear(), easter.getMonth(), easter.getDate() + off);
    } else if (entry?.type === 'anchored-weekday-offset') {
      const anchorDate: Date | null = resolved && entry?.anchorKey ? resolved[entry.anchorKey] || null : null;
      if (!anchorDate) return null;
      const off: number = Number(entry?.weekdayOffset) || 0;
      date = this._walkHolidayWorkdays(anchorDate, off);
    }

    if (date && entry?.weekendRollover) date = this._rollHolidayFromWeekend(date);
    return date;
  }


  private _nthWeekdayOfMonth(year: number, month: number | undefined, weekday: string | undefined, occurrence: string | undefined): Date | null {
    if (!year || !month || !weekday || !occurrence) return null;
    const weekdayIdx: number = HOLIDAY_WEEKDAY_NAMES.indexOf(String(weekday).toLowerCase());
    if (weekdayIdx < 0) return null;

    if (occurrence === 'last') {
      const lastDay: number = new Date(year, month, 0).getDate();
      const last: Date = new Date(year, month - 1, lastDay);
      const back: number = (last.getDay() - weekdayIdx + 7) % 7;
      return new Date(year, month - 1, lastDay - back);
    }

    const n: number = HOLIDAY_NTH_MAP?.[String(occurrence)];
    if (!n) return null;

    const first: Date = new Date(year, month - 1, 1);
    const forward: number = (weekdayIdx - first.getDay() + 7) % 7;
    return new Date(year, month - 1, 1 + forward + (n - 1) * 7);
  }


  private _easterSundayForYear(year: number): Date | null {
    if (!year) return null;
    const a: number = year % 19;
    const b: number = Math.floor(year / 100);
    const c: number = year % 100;
    const d: number = Math.floor(b / 4);
    const e: number = b % 4;
    const f: number = Math.floor((b + 8) / 25);
    const g: number = Math.floor((b - f + 1) / 3);
    const h: number = (19 * a + b - d - g + 15) % 30;
    const i: number = Math.floor(c / 4);
    const k: number = c % 4;
    const l: number = (32 + 2 * e + 2 * i - h - k) % 7;
    const m: number = Math.floor((a + 11 * h + 22 * l) / 451);
    const month: number = Math.floor((h + l - 7 * m + 114) / 31);
    const day: number = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(year, month - 1, day);
  }


  private _walkHolidayWorkdays(date: Date, steps: number): Date | null {
    if (!date) return null;
    const result: Date = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    if (!steps) return result;

    const direction: number = steps > 0 ? 1 : -1;
    let remaining: number = Math.abs(steps);
    while (remaining > 0) {
      result.setDate(result.getDate() + direction);
      const dow: number = result.getDay();
      if (dow !== 0 && dow !== 6) remaining--;
    }
    return result;
  }


  private _rollHolidayFromWeekend(date: Date): Date {
    if (!date) return date;
    const dow: number = date.getDay();
    if (dow === 6) return new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1);
    if (dow === 0) return new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
    return date;
  }


  private _formatHolidayDate(date: Date): string {
    if (!date) return '';
    const y: number = date.getFullYear();
    const m: string = String(date.getMonth() + 1).padStart(2, '0');
    const d: string = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
}